import React, { useState, useContext } from "react";
import ExcelJS from "exceljs";
import { Button, CircularProgress } from "@mui/material";
import { query, where, getDocs } from "firebase/firestore";
import { format } from "date-fns";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";

export default function GeneratePropertyIssuesExcel() {
  const [isGenerating, setIsGenerating] = useState(false);
  const { selectedProperty, selectedClient, propertyInformation } = useContext(PropertyContext);

  const generateExcel = async () => {
    setIsGenerating(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Property Issues");

      // Get property name
      const propertyName = propertyInformation?.displayName || selectedProperty || "Property";
      const generatedDateTime = format(new Date(), "MMMM do, yyyy h:mm:ss a");

      // Set column widths first (without headers to avoid auto-creating header row)
      worksheet.columns = [
        { key: "status", width: 15 },
        { key: "reportedDate", width: 25 },
        { key: "reportedTo", width: 30 },
        { key: "assignedTo", width: 30 },
        { key: "issueDescription", width: 50 },
        { key: "fileCount", width: 12 },
        { key: "attachments", width: 40 },
      ];

      // Row 1: Property name on left, date/time on right
      worksheet.mergeCells("A1:E1");
      const headerRow = worksheet.getRow(1);
      headerRow.getCell(1).value = propertyName;
      headerRow.getCell(1).font = { bold: true, size: 14 };
      headerRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      headerRow.height = 25;

      // Add date/time to the right side
      worksheet.mergeCells("F1:G1");
      headerRow.getCell(6).value = `Generated: ${generatedDateTime}`;
      headerRow.getCell(6).font = { size: 11 };
      headerRow.getCell(6).alignment = { horizontal: "right", vertical: "middle" };

      // Row 2: Empty row for spacing
      worksheet.addRow([]);

      // Row 3: Column headers
      const columnHeaderRow = worksheet.getRow(3);
      columnHeaderRow.values = [
        "Status",
        "Reported Date",
        "Reported To",
        "Assigned To",
        "Issue Description",
        "File Count",
        "Attachments"
      ];
      columnHeaderRow.font = { bold: true, size: 12 };
      columnHeaderRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
      columnHeaderRow.alignment = { horizontal: "center", vertical: "middle" };
      columnHeaderRow.height = 20;

      // Apply borders to column headers
      columnHeaderRow.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Fetch property issues from Firestore
      const issuesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "propertyIssues"
      );
      const q = query(issuesRef, where("completed", "==", false));
      const querySnapshot = await getDocs(q);

      // Add data rows (starting from row 4)
      querySnapshot.docs.forEach((doc) => {
        const issue = doc.data();

        const reportedDate = issue.submittedAt
          ? format(issue.submittedAt.toDate(), "MMMM do, yyyy h:mm:ss a")
          : "N/A";

        const reportedTo = issue.reportTo && issue.reportTo.length > 0
          ? issue.reportTo.join(", ")
          : "N/A";

        const assignedTo = issue.assignedTo || "N/A";

        const fileCount = issue.files ? issue.files.length : 0;

        // Determine how many rows this issue needs (at least 1, or number of files)
        const rowCount = Math.max(1, fileCount);
        const startRowNumber = worksheet.rowCount + 1;

        let photoCount = 0;
        let attachmentCount = 0;

        // Add all rows for this issue
        for (let i = 0; i < rowCount; i++) {
          const newRow = worksheet.addRow([
            issue.status || "N/A",
            reportedDate,
            reportedTo,
            assignedTo,
            issue.issueDetails || "",
            fileCount,
            "" // Will be filled with attachment links
          ]);

          // Style the data row
          newRow.eachCell((cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            cell.alignment = { vertical: "top", wrapText: true };
          });

          // Add hyperlink to the attachment cell (column 7) for this row
          if (issue.files && issue.files[i]) {
            const file = issue.files[i];
            const url = file.url || "";
            const isPhoto =
              url.toLowerCase().includes(".jpg") ||
              url.toLowerCase().includes(".jpeg") ||
              url.toLowerCase().includes(".png") ||
              url.toLowerCase().includes(".gif");

            let label;
            if (isPhoto) {
              photoCount++;
              label = `Photo ${photoCount}`;
            } else {
              attachmentCount++;
              label = `Attachment ${attachmentCount}`;
            }

            const attachmentsCell = newRow.getCell(7);
            attachmentsCell.value = {
              text: label,
              hyperlink: url,
              tooltip: `Click to open ${label}`
            };
            attachmentsCell.font = {
              color: { argb: "FF0563C1" },
              underline: true
            };
          }
        }

        // Merge cells for columns 1-6 if there are multiple rows
        if (rowCount > 1) {
          const endRowNumber = startRowNumber + rowCount - 1;
          for (let col = 1; col <= 6; col++) {
            worksheet.mergeCells(startRowNumber, col, endRowNumber, col);
            // Center the content vertically in merged cells
            const mergedCell = worksheet.getCell(startRowNumber, col);
            mergedCell.alignment = { vertical: "middle", wrapText: true };
          }
        }
      });

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(
        blob,
        buildReportFilename("Property_Issues_Report", { ext: "xlsx" })
      );
    } catch (error) {
      console.error("Error generating Excel report:", error);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generateExcel}
      disabled={isGenerating}
      startIcon={isGenerating ? <CircularProgress size={16} /> : null}
    >
      {isGenerating ? "Generating..." : "Property Issues (Excel)"}
    </Button>
  );
}
